import type { DocumentStatus, DocumentType } from "@/features/inventory/api/inventory.types";

/**
 * The vocabulary of a stock document, in the words a warehouse actually uses.
 *
 * THE DISTINCTION THIS FILE EXISTS TO PROTECT is `CANCELLED` against `REVERSED`. They are not
 * two words for the same ending: a cancelled document never touched the ledger and leaves no
 * movement behind; a reversed one did, and was undone by a counter-entry that stands in the
 * history beside it. A screen that offers one "İptal" button for both hides from the operator
 * whether stock moved — which is the one thing they cannot work out afterwards from the list.
 */

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  RECEIPT: "Mal kabul",
  ISSUE: "Çıkış",
  TRANSFER_OUT: "Transfer çıkış",
  TRANSFER_IN: "Transfer giriş",
  ADJUSTMENT: "Düzeltme",
  RETURN_IN: "İade girişi",
  RETURN_OUT: "İade çıkışı",
};

export const DOCUMENT_TYPE_OPTIONS = Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

export const DOCUMENT_STATUS_LABELS: Record<DocumentStatus, string> = {
  DRAFT: "Taslak",
  SUBMITTED: "Onay bekliyor",
  APPROVED: "Onaylandı",
  POSTED: "İşlendi",
  REVERSED: "Ters kayıtla geri alındı",
  CANCELLED: "İptal edildi",
};

export const DOCUMENT_STATUS_OPTIONS = Object.entries(DOCUMENT_STATUS_LABELS).map(
  ([value, label]) => ({ value, label }),
);

/**
 * `POSTED` is the only green: it is the state in which the ledger changed and the stock is
 * real. `APPROVED` is somebody's yes, not a movement. `REVERSED` warns rather than alarms —
 * it is a correct, deliberate correction that left two entries in the history.
 */
export const DOCUMENT_STATUS_TONE: Record<
  DocumentStatus,
  "neutral" | "info" | "success" | "warning"
> = {
  DRAFT: "neutral",
  SUBMITTED: "warning",
  APPROVED: "info",
  POSTED: "success",
  REVERSED: "warning",
  CANCELLED: "neutral",
};

/**
 * What each status has DONE to stock so far, for the document card's status banner. Not what
 * somebody could do next — this screen does not edit or withdraw documents, and a hint that said
 * «serbestçe düzenlenebilir» promised a form that is not here (second review, 7 Ekim 2026).
 */
export const DOCUMENT_STATUS_HINTS: Record<DocumentStatus, string> = {
  DRAFT: "Taslak; deftere işlenmedi, stok henüz değişmedi.",
  SUBMITTED: "Onay bekliyor; stok henüz değişmedi.",
  APPROVED: "Onaylandı ama deftere işlenmedi — stok henüz değişmedi.",
  POSTED: "Deftere işlendi. Stok bu belgeyle değişti.",
  REVERSED: "Deftere işlenmişti; karşı kayıtla geri alındı. Her iki hareket de geçmişte durur.",
  CANCELLED: "Deftere hiç işlenmeden kapatıldı. Stokta izi yok.",
};

/**
 * Whether a document's movement is INTO the warehouse it names. Used for the sign the list
 * shows beside a quantity: the same 40 units mean opposite things on a receipt and an issue,
 * and a list that shows both as "40" makes the reader do the translation every row.
 */
export const DOCUMENT_TYPE_DIRECTION: Record<DocumentType, "in" | "out" | "move"> = {
  RECEIPT: "in",
  RETURN_IN: "in",
  ISSUE: "out",
  RETURN_OUT: "out",
  TRANSFER_OUT: "move",
  TRANSFER_IN: "move",
  ADJUSTMENT: "move",
};

/**
 * «DEFTERE İŞLE»NİN ETKİSİ, SAYIYLA (4. tur, 9 Ekim 2026).
 *
 * Pencere «Stok bu belgeyle değişir» diyordu — hangi depoda, kaç kalem, ne kadar söylemeden
 * (`crud-surfaces.md` §8: «etki cümlesi sayı söyler»). Toplam yalnız bütün satırlar tek ürünse
 * yazılıyor: iki ayrı ilacın kutuları toplanmaz (kartın kendi kuralı).
 */
export function documentPostImpact(input: {
  documentType: DocumentType;
  warehouseName: string | null;
  destinationName: string | null;
  lineCount: number;
  /** «580 kutu» when every line is one article; otherwise null. */
  total: string | null;
}): string {
  const where = `«${input.warehouseName ?? "seçili depo"}»`;
  const amount = input.total ? ` (toplam ${input.total})` : "";
  const lines = `${input.lineCount} kalem`;
  const direction = DOCUMENT_TYPE_DIRECTION[input.documentType];
  const first =
    input.destinationName !== null
      ? `${where} → «${input.destinationName}»: ${lines} transfer deftere yazılır${amount}.`
      : direction === "in"
        ? `${where} için ${lines} stok girişi deftere yazılır${amount}.`
        : direction === "out"
          ? `${where} için ${lines} stok çıkışı deftere yazılır${amount}.`
          : `${where} için ${lines} stok hareketi deftere yazılır${amount}.`;
  return `${first} İşlendikten sonra belge iptal edilemez; hareketleri defterde kalıcıdır.`;
}
